import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const RISK_04: GgxTourDefinition = {
    key: '[RISK-04] Measuring Accuracy',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 10,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Is the chatbot actually right?',
        subtitle:
            'The credit card chatbot answers quickly and politely. That is not the same as ' +
            'answering correctly, and a wrong balance or a made-up fee is what costs trust. ' +
            'This tour takes one risk, accuracy, and shows how it is measured end to end: what ' +
            'accuracy means for different kinds of question, how facts are checked against ' +
            'ground truth, how an LLM judges relevance, and how out-of-scope questions are ' +
            'scored. Along the way you will read a real accuracy report on the pipeline.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen accuracy measured from three angles: factual accuracy by comparing ' +
            'the card details in a reply with ground truth, answer relevancy by asking an LLM ' +
            'judge whether the reply fits the question, and appropriateness for questions the ' +
            'bot should not answer at all. You also saw how judges are validated against human ' +
            'scores and made more reliable, and where an unnecessary data lookup shows up as ' +
            'its own metric. Next, Running your first evaluation shows how to produce a report ' +
            'like this one yourself.',
        learned: [
            '**Factual accuracy** compares card details in a reply with ground truth.',
            '**Answer relevancy** asks an LLM judge whether the reply fits.',
            'Judges are **validated against human scores** before they are trusted.',
        ],
    },
    chapters: [
        {
            name: 'Find the pipeline',
            blurb: 'Search from anywhere and jump straight to it',
            icon: 'search',
            steps: [2, 3, 4],
        },
        {
            name: 'What accuracy means here',
            blurb: 'Three kinds of question, and the prompt behind the answers',
            icon: 'cap',
            steps: [5, 6],
        },
        {
            name: 'Read the report',
            blurb: 'The executive summary and factual accuracy',
            icon: 'grid',
            steps: [7, 8, 9, 10],
        },
        {
            name: 'Judging relevance',
            blurb: 'LLM-as-a-Judge, and how to trust it',
            icon: 'pulse',
            steps: [11, 12, 13, 14, 15],
        },
        {
            name: 'Beyond the happy path',
            blurb: 'Unnecessary lookups and out-of-scope questions',
            icon: 'compass',
            steps: [16, 17, 18],
        },
    ],
    steps: [
        {
            target: '[test-id="search-button"]',
            title: 'Start from the search box',
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'We will read a real accuracy report, so let us find the pipeline first. The ' +
                'global search finds any object by name from any page, and ' +
                '<strong>Ctrl+K</strong> opens it too. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the chatbot',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'Combined Customer Intent and Response Pipeline v1',
            scrollIntoView: true,
            title: 'Pick the pipeline itself',
            whatHappens: "the pipeline's page opens on its Details tab.",
            icon: 'search',
            description:
                'A few objects share this name. The one we want is the pipeline marked ' +
                '<strong>v1</strong>. Click <strong>Combined Customer Intent and Response ' +
                'Pipeline</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline',
        },
        {
            title: 'Accuracy depends on the question',
            whatHappens: 'next, the prompt that produces these answers.',
            icon: 'cap',
            description:
                'There is no single accuracy score for a chatbot, because customers ask three ' +
                'kinds of thing. The graphic lays them out side by side; open each tab for ' +
                'how it is judged, then click Continue.',
            tabs: [
                {
                    label: 'Needs data',
                    html:
                        '<p data-ggx-lead>In-scope questions that need customer data, like ' +
                        '"what is the balance on my VISA?".</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Factual accuracy</strong>: are the card facts in the ' +
                        'reply right?</li>' +
                        '<li><strong>Answer relevancy</strong>: does the reply make sense for ' +
                        'the question?</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="In practice">This is where a wrong balance or a ' +
                        'made-up fee shows up, so it gets the strictest check.</aside>',
                },
                {
                    label: 'Needs no data',
                    html:
                        '<p data-ggx-lead>In-scope questions that need no customer data, like ' +
                        '"can you block my card?".</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Relevance</strong> of the reply, and whether any statement ' +
                        'it makes is true.</li>' +
                        '<li><strong>No unnecessary lookup</strong>: ideally the bot did not ' +
                        'fetch data it did not need.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="In practice">A needless lookup is not a wrong ' +
                        'answer, but it adds latency and gives the model more to misuse.</aside>',
                },
                {
                    label: 'Out of scope',
                    html:
                        '<p data-ggx-lead>Questions the bot should not answer at all, like the ' +
                        'weather or a loan application.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Appropriateness</strong>: did the bot decline the way it ' +
                        'was designed to, and point the customer somewhere useful?</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="In practice">A polite, on-brand refusal is the ' +
                        'correct answer here; a helpful-sounding guess is a failure.</aside>',
                },
            ],
            image: {
                src: 'https://genguardx.ai/tours/risk-04-accuracy-by-query.png',
                title: 'Overview of accuracy measurements by query type',
                caption: 'How accuracy is measured for each kind of customer question.',
            },
        },
        {
            title: 'First, the prompt behind the answers',
            whatHappens: 'next, the dashboard where the accuracy reports live.',
            icon: 'cap',
            description:
                'Before reading the numbers it helps to know what the response model was told ' +
                'to do. The <strong>Customer Credit Card-Related Response Prompt</strong> has ' +
                'six parts. This short video shows how to reach it from the pipeline lineage ' +
                'view and what each part does. Watch it, then click Continue.',
            benefits: ['Examples', 'Context', 'Task', 'Tone', 'Persona and goal', 'Output format'],
            video: {
                src: 'https://genguardx.ai/tours/risk-04-response-prompt.mp4',
                title: 'Exploring the Customer Credit Card-Related Response Prompt',
            },
        },
        {
            target: '[test-id="tab-item-Dashboard"]',
            scrollIntoView: true,
            title: 'Where the reports live',
            whatHappens: 'the Dashboard opens on the latest run, with one tab per report.',
            icon: 'grid',
            description:
                'Every evaluation run on this pipeline produces a dashboard with one tab per ' +
                'report. We will read <strong>Response Accuracy (Credit Card Queries)</strong>, ' +
                'the report for in-scope questions. Click <strong>Dashboard</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Dashboard',
        },
        {
            target: '[test-id="figure-Executive Summary"] h1',
            frame: true,
            scrollIntoView: true,
            title: 'Start with the executive summary',
            whatHappens: 'next, how factual accuracy is checked.',
            icon: 'grid',
            description:
                'Every GGX report opens the same way: a summary that states the ' +
                '<strong>testing methodology</strong>, explains each metric, gives a guide to ' +
                'reading positive and negative indicators, and shows the headline numbers. ' +
                'Read this first and the charts below make sense. This report measures three ' +
                'things:' +
                '<ul data-ggx-list>' +
                '<li><strong>Answer relevancy</strong> for every in-scope question.</li>' +
                '<li><strong>Factual accuracy</strong> for questions that use card data.</li>' +
                '<li><strong>Unnecessary fact retrieval</strong>: how often the bot fetched ' +
                'data it did not need.</li>' +
                '</ul>' +
                'Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="figure-Executive Summary"] .info-box',
            targetText: 'Factual Accuracy',
            frame: true,
            scrollIntoView: true,
            title: 'Checking the facts against ground truth',
            whatHappens: 'next, how this check can be extended.',
            icon: 'grid',
            description:
                '<strong>Factual accuracy</strong> is a rule-based comparison: the card ' +
                'details in the reply against the ground truth for that customer. When the ' +
                'reply carries tagged values, the comparison is direct. When it is plain text, ' +
                'the card type, last four digits and balance are extracted first, then compared. ' +
                'Each reply lands in one of three buckets: fully correct, partially correct, or ' +
                'incorrect. The graphic shows both paths. Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-04-factual-accuracy.png',
                title: 'Factual accuracy testing with rule-based comparisons',
                caption: 'Tagged data compared directly; plain text extracted first.',
            },
            side: 'top',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            title: 'The same check, stretched further',
            whatHappens: 'next, judging whether an answer is relevant.',
            icon: 'list',
            description:
                'The basic comparison covers single questions with one right answer. Real ' +
                'conversations need a little more:' +
                '<ul data-ggx-list>' +
                '<li><strong>Multi-turn questions</strong>: pass the chat history as context ' +
                'and let the ground truth include facts from earlier turns.</li>' +
                '<li><strong>Computed values</strong>: tag the calculation used, such as the ' +
                'sum of balances across cards, and apply the same one to the ground truth.</li>' +
                '<li><strong>Several acceptable answers</strong>: compare against each and ' +
                'keep the best match.</li>' +
                '<li><strong>Several questions in one message</strong>: decide what the ' +
                'pipeline should do (answer in order, or only the first) and score against ' +
                'that.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">Each extension is a rule you write once in ' +
                'the report definition; every pipeline evaluated with that report gets it for ' +
                'free.</aside>' +
                'Click Continue.',
        },
        {
            target: '[test-id="figure-Answer Relevancy by Intent (User Specific Questions)"]',
            frame: true,
            scrollIntoView: true,
            title: 'Is the answer even about the question?',
            whatHappens: 'next, the judge that produced these scores.',
            icon: 'pulse',
            description:
                'A reply can have every fact right and still miss the point. ' +
                '<strong>Answer relevancy</strong> asks whether the reply fits the question. ' +
                'This chart shows it by intent for questions that use customer data, and the ' +
                'graphic compares the two ways to check it:' +
                '<ul data-ggx-list>' +
                '<li><strong>Against golden answers</strong>: compare the reply with one or ' +
                'more model answers, using embeddings for simple replies.</li>' +
                '<li><strong>Against the question</strong>: ask whether the reply is valid ' +
                'given the question and the prompt instructions. Needs no golden answers, so ' +
                'it can double as a guardrail.</li>' +
                '</ul>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-04-appropriateness.png',
                title: 'Two common approaches for evaluating answer appropriateness',
                caption: 'Semantic match against golden answers, or validity against the query.',
            },
            side: 'right',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="figure-Answer Relevancy by Intent (User Specific Questions)"] [test-id="evaluation-methodology-badge"]',
            scrollIntoView: true,
            title: 'Meet the judge',
            whatHappens: 'next, what to keep in mind about judges.',
            icon: 'pulse',
            description:
                'This tag says how the metric was scored: an <strong>LLM-as-a-Judge</strong>, ' +
                'a model given the question, the reply and a rubric, asked to rate relevance ' +
                'from 1 to 5. The graphic shows the judge at work and the check that makes it ' +
                'trustworthy.' +
                '<aside data-ggx-use="How to trust it">Compare its scores with human scores on ' +
                'the same replies. When they line up along the diagonal of a confusion matrix, ' +
                'the judge is behaving like a careful reviewer. When they do not, the prompt or ' +
                'the thresholds get adjusted.</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-04-judge.png',
                title: 'Implementation of LLM-as-a-Judge for evaluating answer relevancy',
                caption: 'The judge scoring replies, and its scores validated against humans.',
            },
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            title: 'What a judge is good at, and where it is not',
            whatHappens: 'next, ways to make a judge more reliable.',
            icon: 'pulse',
            description:
                '<ul data-ggx-list>' +
                '<li><strong>Versatile</strong>: far more than older NLP or embedding-based ' +
                'scorers. For nuanced checks on a complex application they are often the only ' +
                'practical way to imitate human scoring, fast and cheap compared with ' +
                'people.</li>' +
                '<li><strong>Not perfect</strong>: they can be biased, sensitive to how the ' +
                'prompt is worded, and they can hallucinate a judgement, so they need ' +
                'evaluating themselves.</li>' +
                '<li><strong>Best on narrow checks</strong>: more reliable on a specific ' +
                'question than a broad "is this good?".</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">Use a judge as a supplement to human ' +
                'testing during development and validation, not a replacement for it.</aside>' +
                'Click Continue.',
        },
        {
            title: 'Making a judge more reliable',
            whatHappens: 'next, where the judge itself is configured.',
            icon: 'list',
            description:
                'Four techniques do most of the work:' +
                '<ul data-ggx-list>' +
                '<li><strong>Account for confidence</strong>: G-Eval weights the numeric score ' +
                'by the probability the model assigns it, giving a finer-grained result.</li>' +
                '<li><strong>Decompose the question</strong>: ask smaller yes/no or checklist ' +
                'questions, generate targeted sub-questions from the content, or run a ' +
                'structured flow of checks.</li>' +
                '<li><strong>Go hybrid</strong>: let the LLM extract or judge, then apply ' +
                'deterministic checks to what it produced.</li>' +
                '<li><strong>Use several judges</strong>: different prompts or different ' +
                'models, then combine them.</li>' +
                '</ul>' +
                '<aside data-ggx-use="Go deeper">Using Models as Evaluators in the Learning ' +
                'Hub opens a real judge and the report that scores it against people.</aside>' +
                'Click Continue.',
        },
        {
            title: 'Where the judge lives in GGX',
            whatHappens: 'next, a metric about fetching data you did not need.',
            icon: 'pulse',
            description:
                'A judge is just another model in GGX, with its own prompt, settings and ' +
                'reports, so it is reviewed and approved like anything else. This short video ' +
                'shows how to find the LLM-as-a-Judge configuration through the Models page ' +
                'and read its prompt and settings. Watch it, then click Continue.',
            video: {
                src: 'https://genguardx.ai/tours/llm-judge-model-catalog.mp4',
                title: 'Finding the LLM-as-a-Judge configuration on the Models page',
            },
        },
        {
            target: '[test-id="figure-Unnecessary Fact Retrieval by Intent (General Questions)"]',
            frame: true,
            scrollIntoView: true,
            title: 'Did it look things up for no reason?',
            whatHappens: 'next, the report for questions outside the credit card scope.',
            icon: 'compass',
            description:
                'Some questions need no customer data at all: "are you able to block a card?" ' +
                'can be answered without touching the database. This chart shows, intent by ' +
                'intent, how often the pipeline fetched card details anyway. An unnecessary ' +
                'lookup does not make the answer wrong, but it adds latency and gives the ' +
                'model more material to misuse, so it is worth tracking on its own. Click ' +
                'Continue.',
            side: 'left',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id^="jobtab-Response Accuracy (Non Credit"]',
            scrollIntoView: true,
            title: 'Now the questions it should not answer',
            whatHappens: 'the report for non-credit card queries opens.',
            icon: 'compass',
            description:
                'Accuracy also covers what the bot does when a customer asks about the ' +
                'weather, or anything else outside its scope. That has its own report. Click ' +
                '<strong>Response Accuracy (Non Credit-Card Queries)</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Response Accuracy (Non Credit-Card Queries)',
        },
        {
            target: '[test-id="figure-Executive Summary"] h1',
            frame: true,
            scrollIntoView: true,
            title: 'Scoring a good "no"',
            whatHappens: 'the tour wraps up.',
            icon: 'compass',
            description:
                'For out-of-scope questions the right answer is a templated response: "I can ' +
                'connect you with a customer service representative", "I cannot process this ' +
                'request", and so on, either written into the prompt or hard-coded in the ' +
                'pipeline. Those templates are the ground truth, and the graphic shows two ' +
                'ways to score against them:' +
                '<ul data-ggx-list>' +
                '<li><strong>Simple</strong>: embedding similarity between the reply and the ' +
                'expected template, plus human review.</li>' +
                '<li><strong>Stricter</strong>: exact match first, then semantic and edit ' +
                'distance, with the poor matches sent to a person or a judge.</li>' +
                '</ul>' +
                'Click Continue to finish.',
            image: {
                src: 'https://genguardx.ai/tours/risk-04-out-of-context.png',
                title: 'Evaluating appropriateness of responses to out-of-context queries',
                caption: 'Templated ground truth, and the simple and stricter evaluation paths.',
            },
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
